
// @ts-ignore
    import __i18nConfig from '@next-translate-root/i18n'
// @ts-ignore
    import __appWithI18n from 'next-translate/appWithI18n'
// @ts-ignore
    
import Head from 'next/head'
import singletonRouter, { useRouter } from 'next/router'
import { useEffect, useMemo, useState } from 'react'
import { InstantSearch } from 'react-instantsearch'
import { createInstantSearchRouterNext } from 'react-instantsearch-hooks-router-nextjs'

import { MultipleQueriesQuery } from '@algolia/client-search'
import { Country as CountryList } from 'country-list'
import { InsightsEvent } from 'instantsearch.js/es/types/index'
import { debounce, isEmpty } from 'lodash'

import Container from 'layouts/container'

import FilterGroup from 'components/_pages/search/filter-group'
import SearchHeader from 'components/_pages/search/search-header'
import SearchPageConfigure from 'components/_pages/search/search-page-configure'
import { algoliaQueryEmptyResponse } from 'components/_pages/search/utils'
import { Banner } from 'components/molecules/banner'
import CarouselSlider from 'components/molecules/carousel-slider/carousel-slider'
import HitResultCounts from 'components/molecules/hit-result-counts'
import HitsSortBy from 'components/molecules/hits-sort-by'
import HitsViewBy from 'components/molecules/hits-view-by'
import { HitsViewByType } from 'components/molecules/hits-view-by/constants'
import PaginationCustomized from 'components/molecules/pagination'
import CurrentRefinementsCustomized from 'components/molecules/refinement-current-customized'
import SearchBoxVirtual from 'components/molecules/search-box-virtual'
import HitBrandList from 'components/organisms/hit-brand-list'
import HitProductList from 'components/organisms/hit-product-list'
import { useGetNavMenu } from 'features/layout/header/navbar/use-get-nav-menu'

import { useTranslate } from 'hooks'
import { useCountries } from 'hooks/brand/use-countries'
import useUniversalPriceConversion, {
  FACTOR_FROM_CENT_TO_UNIT_CURRENCY,
} from 'hooks/currency/use-universial-price-conversion'
import { Country } from 'hooks/retailer/use-countries'
import { CarouselBanner } from 'hooks/retailer/use-get-carousel-banners'

import { getMapFromArrayByKey } from 'utils/data-structure'
import { MAP_ALGOLIA_INSIGHTS_TO_GTM_TRIGGER } from 'utils/search/insights'
import { searchClient } from 'utils/search/search-client'
import {
  ExtendedSearchUIState,
  SearchRouteState,
  decodeDiscoverQuery,
  getStateMapping,
} from 'utils/search/search-url-mapping'
import { fireTrackingEvent } from 'utils/tracking'
import { countries as countriesList } from 'utils/use-countries-list'

import {
  INSTANT_SEARCH_BRAND_REPLICAS_INDEX,
  PRIMARY_SEARCH_INDEX,
  QUERY_SORT_BY_PARAM_NAME,
} from 'constants/instantSearch'
import { ROUTES } from 'constants/routes'

import css from './search.module.scss'

export const TERM_SHOP_ALL = 'shop-all'
export const TERM_NEW_PRODUCTS = 'new-products'
export const TERM_NEW_KEY = 'new-key'
export const TERM_PRODUCTS = 'products'
export const TERM_PROMOTION = 'promotion'

const Search = ({ openSignUp }: { openSignUp?: () => void }) => {
  const { t } = useTranslate('strapi-search-[...term]')
  const router = useRouter()
  const { data: countries } = useCountries()
  const { rate, currency } = useUniversalPriceConversion()
  const { data: navMenuItemList } = useGetNavMenu()

  const [pageTitle, setPageTitle] = useState(t('PageSearch.PageSearch_Filters_default_page_title'))
  const [banners, setBanners] = useState<CarouselBanner[]>([])

  const isPromotionRoute = router.asPath.includes(ROUTES.SEARCH_PROMOTION)
  const isDiscoverRoute = router.asPath.includes('discover') // checkout rewrites in next.config.js

  useEffect(() => {
    if (isPromotionRoute && !isEmpty(navMenuItemList)) {
      const params = router.query.terms as string[]
      const promotionKey = params[1]

      const promotion = navMenuItemList?.find((nav) => nav.key === promotionKey && nav.tag === 'promotion')

      if (!promotion) {
        void router.push(ROUTES.SEARCH_PRODUCTS)
        return
      }
      setBanners([
        {
          imgUrl: promotion.imageLinks[0]
            ? promotion.imageLinks[0].image?.url ?? ''
            : promotion.categoryNav?.promotion?.imageUrl ?? '',
          imgUrlMobile: promotion.imageLinks[0]
            ? promotion.imageLinks[0].imageMobile?.url ?? ''
            : promotion.categoryNav?.promotion?.imageUrl ?? '',
          name: promotion.name,
        },
      ])
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navMenuItemList])

  const indexQueryName = router.query[QUERY_SORT_BY_PARAM_NAME]

  const isSortByBrand = Object.values(INSTANT_SEARCH_BRAND_REPLICAS_INDEX).some(({ name }) => name === indexQueryName)

  const [viewBy, setViewBy] = useState<HitsViewByType>(isSortByBrand ? 'BRAND' : 'PRODUCT')

  useEffect(() => {
    setViewBy(isSortByBrand ? 'BRAND' : 'PRODUCT')
  }, [isSortByBrand])

  const countriesByCode = useMemo(() => {
    if (isEmpty(countries)) return {}

    const countriesListByCode = getMapFromArrayByKey<CountryList>(countriesList, 'code')
    const countriesByCodeFromApi = getMapFromArrayByKey<Country>(countries ?? [], 'countryCode')

    return { ...countriesListByCode, ...countriesByCodeFromApi }
  }, [countries])

  const discoverPageTitle = t('PageSearch.PageSearch_discover_title').replaceAll(
    '{{key}}',
    decodeDiscoverQuery(router.query.query as string)
  )

  return (
    <>
      <Head>{isDiscoverRoute ? <title>{discoverPageTitle}</title> : <title>{pageTitle}</title>}</Head>
      <main>
        {!!isDiscoverRoute && (
          <Banner
            backgroundImage="/images/lp/footer-banner.jpg"
            mainText={discoverPageTitle}
            secondaryText={t('PageSearch.PageSearch_discover_desc')}
            button={{
              name: t('PageSearch.PageSearch_discover_cta'),
              buttonFunction: () => openSignUp?.(),
              trackName: 'cta_home_top_banner_sell',
              testId: 'cta_home_top_banner_sell',
              buttonColor: 'black',
            }}
            size="small"
          />
        )}
        <InstantSearch<ExtendedSearchUIState, SearchRouteState>
          searchClient={{
            ...searchClient,
            search: (requests: readonly MultipleQueriesQuery[]) => {
              const allRequestsWithoutHitsPerPage = requests.every(
                ({ params }) => !params?.hitsPerPage || params.hitsPerPage === 0
              )
              if (allRequestsWithoutHitsPerPage) {
                return algoliaQueryEmptyResponse(requests)
              }
              return searchClient.search(requests)
            },
          }}
          indexName={PRIMARY_SEARCH_INDEX}
          routing={{
            router: createInstantSearchRouterNext({ singletonRouter }),
            stateMapping: getStateMapping(),
          }}
          key={(router.query.query as string) || ''}
          future={{
            preserveSharedStateOnUnmount: false,
          }}
          onStateChange={({ uiState, setUiState }) => {
            return setUiState(uiState)
          }}
          insights={{
            // prevent duplicated onEvent trigger due to duplicated search call
            onEvent: debounce((event: InsightsEvent) => {
              const { insightsMethod, payload } = event

              if (!insightsMethod) return

              fireTrackingEvent('track', {
                ...payload,
                event: MAP_ALGOLIA_INSIGHTS_TO_GTM_TRIGGER[insightsMethod],
                insightsMethod,
              })
            }, 200),
          }}
        >
          <SearchPageConfigure />
          <SearchBoxVirtual />

          <div className={css.pageContainer}>
            {!isDiscoverRoute && (
              <>
                <Container className={css.topSection}>
                  <SearchHeader setPageTitle={setPageTitle} isPromotionRoute={isPromotionRoute} />
                </Container>
                <Container>
                  {!!isPromotionRoute && banners.length > 0 && <CarouselSlider banners={banners} isLoading={false} />}
                </Container>
                <Container className={css['viewByWrapper-mobile']}>
                  <HitsViewBy viewBy={viewBy} onChangeViewBy={setViewBy} />
                </Container>
              </>
            )}

            <Container className={css.mainContent}>
              <FilterGroup
                viewBy={viewBy}
                rate={rate}
                currency={currency}
                countriesByCode={countriesByCode}
                isPromotionRoute={isPromotionRoute}
              />
              <div aria-labelledby="product-list" className={css.productListWrapper} data-test-id="product-list">
                <section className={css.listTopSection}>
                  <div className={css.viewByWrapper}>
                    <HitsViewBy viewBy={viewBy} onChangeViewBy={setViewBy} />
                  </div>

                  <div className={css.resultStatsWrapper}>
                    <HitResultCounts />
                    <div className={css.sortByWrapper}>
                      <HitsSortBy viewBy={viewBy} />
                    </div>
                  </div>
                </section>
                <section className="border-t border-gray-200 sm:border-0 flex-grow sm:flex sm:items-center">
                  <div aria-hidden="true" className="hidden h-5 w-px bg-gray-300 sm:ml-4 sm:block" />
                  <CurrentRefinementsCustomized
                    wholeSalePriceRate={rate * FACTOR_FROM_CENT_TO_UNIT_CURRENCY}
                    movSalePriceRate={rate}
                    currency={currency}
                    countriesByCode={countriesByCode}
                  />
                </section>
                {viewBy === 'PRODUCT' ? (
                  <HitProductList openSignUp={openSignUp} />
                ) : (
                  <HitBrandList currency={currency} openSignUp={openSignUp} />
                )}
                <PaginationCustomized />
              </div>
            </Container>
          </div>
        </InstantSearch>
      </main>
    </>
  )
}

const __Page_Next_Translate__ = Search


// @ts-ignore
    export default __appWithI18n(__Page_Next_Translate__, {
// @ts-ignore
      ...__i18nConfig,
// @ts-ignore
      isLoader: true,
// @ts-ignore
      skipInitialProps: false,
// @ts-ignore
      
// @ts-ignore
    });
// @ts-ignore
  